import type {
  AddPropertyMethod,
  AssetDraft,
  PropertyDetails,
  WizardRoom,
} from "@/src/constants/propertyWizard";
import type { CategoryChild } from "@/src/types/category.types";
import type {
  CompletePropertyWizardPayload,
  PropertyCreationSource,
  WizardAssetPayload,
} from "@/src/types/propertyApi.types";
import { buildWizardPropertyPayloadFromDetails } from "@/src/utils/buildWizardPropertyPayload";

const CREATION_SOURCE_BY_METHOD: Record<AddPropertyMethod, PropertyCreationSource> = {
  address: "ADDRESS",
  floorPlan: "FLOOR_PLAN",
  manual: "MANUAL",
};

const resolveCategoryIds = (
  childCategories: CategoryChild[],
  childCategoryId: string,
): { parentCategoryId: string; childCategoryId: string } | null => {
  const category = childCategories.find((item) => item.id === childCategoryId);
  if (!category) {
    return null;
  }
  return {
    parentCategoryId: category.parentCategoryId,
    childCategoryId: category.id,
  };
};

const mapAssetsToPayload = (
  assets: AssetDraft[],
  childCategories: CategoryChild[],
): WizardAssetPayload[] => {
  const mapped: WizardAssetPayload[] = [];

  for (const asset of assets) {
    const categoryIds = resolveCategoryIds(childCategories, asset.childCategoryId);
    if (!categoryIds) {
      continue;
    }

    const payload: WizardAssetPayload = {
      roomName: asset.room,
      parentCategoryId: categoryIds.parentCategoryId,
      childCategoryId: categoryIds.childCategoryId,
      name: asset.name.trim(),
      status: "HEALTHY",
    };

    const notes = asset.notes.trim();
    if (notes) {
      payload.notes = notes;
    }

    mapped.push(payload);
  }

  return mapped;
};

export type BuildCompletePropertyWizardPayloadParams = {
  addMethod: AddPropertyMethod;
  details: PropertyDetails;
  rooms: WizardRoom[];
  assets: AssetDraft[];
  childCategories: CategoryChild[];
  setActive?: boolean;
};

export const buildCompletePropertyWizardPayload = ({
  addMethod,
  details,
  rooms,
  assets,
  childCategories,
  setActive = false,
}: BuildCompletePropertyWizardPayloadParams): CompletePropertyWizardPayload => {
  const mappedAssets = mapAssetsToPayload(assets, childCategories);

  return {
    creationSource: CREATION_SOURCE_BY_METHOD[addMethod],
    setActive,
    property: buildWizardPropertyPayloadFromDetails(details),
    rooms: rooms.map((room, index) => ({
      name: room.name.trim(),
      sortOrder: index + 1,
    })),
    ...(mappedAssets.length > 0 ? { assets: mappedAssets } : {}),
  };
};
